import type { ReactNode } from "react";
import type { DialogBaseProps } from "../DialogBaseProps";
import type { DialogResult } from "../DialogResult";
import { LAYER } from "../../Theme/layers";

/**
 * Width tokens for Dialog. Used by both inline and modal rendering, so
 * keeping it in one module prevents the two paths from drifting silently.
 */
export const dialogWidthValues: Record<string, string> = {
  narrow: "32rem",
  default: "42rem",
  medium: "45rem",
  wide: "56rem",
  "extra-wide": "72rem",
  full: "calc(100vw - 3rem)",
  auto: "fit-content",
};

type DialogWidth = keyof typeof dialogWidthValues;

interface ConfirmOnOkConfig {
  title: string;
  message: string;
  confirmLabel?: string;
}

/**
 * Public Dialog props. Shared between the public `Dialog` wrapper and the
 * internal `DialogInner_` rendering so adding a new prop only requires one
 * edit. `DialogInnerProps` adds `stackId` on top of these.
 */
export interface DialogProps extends DialogBaseProps {
  children: ReactNode;
  footer?: ReactNode;
  width?: DialogWidth;
  title?: ReactNode;
  description?: ReactNode;
  /** Warning message displayed prominently below the title */
  warning?: string;
  /** Filter controls shown inline in the header row (via ControlBar). */
  headerFilters?: ReactNode;
  /** Action buttons shown to the right of the title (left of close/peek buttons). */
  headerActions?: ReactNode;
  /** Menu items — auto-wrapped in a Menu kebab to the right of the title. Pass Button children directly. */
  menu?: ReactNode;
  /** Full-width filter/control bar rendered below the title row, separated by a border. */
  filterBar?: ReactNode;
  /** Stretch dialog to fill most of the viewport height. Useful for data-heavy dialogs. */
  fullHeight?: boolean;
  /** Hide the close button in the top-right corner */
  hideCloseButton?: boolean;
  onOk?: () => void | DialogResult | Promise<void | DialogResult>;
  onCancel?: () => void;
  okLoading?: boolean;
  okText?: string;
  cancelText?: string;
  /** Show a confirmation dialog before executing onOk */
  confirmOnOk?: ConfirmOnOkConfig | undefined;
  /**
   * When true, the OK button stays disabled until any input inside the
   * dialog calls markDirty. Forms set this; other dialogs leave it off
   * so their primary action stays actionable.
   */
  okDisabledIfPristine?: boolean;
  okDisabled?: boolean;
  /**
   * Why the primary action is unavailable, on its hover. Defaults to "No
   * changes to save", which is right for an edit dialog and wrong for one that
   * is waiting on a choice — say what this dialog is actually waiting for.
   */
  okDisabledMessage?: string;
  /**
   * Opt out of the "Discard changes?" confirm on close. Use when the
   * draft is already being persisted outside the dialog (e.g. live
   * sync into a context store / localStorage), so closing isn't a
   * data-loss event — there is nothing to discard.
   */
  draftPersistedExternally?: boolean;
}

/**
 * Inline-mode renders statically for Storybook docs. fullHeight (capped to
 * viewport) and hideCloseButton (controls behavior of dismissable buttons
 * the inline path doesn't render) have no meaning there. Excluding them
 * from the type makes the compiler reject misuse instead of silently
 * dropping the prop.
 */
export type DialogInlineProps = Omit<
  DialogProps,
  "fullHeight" | "hideCloseButton"
>;

export type DialogInnerProps = DialogProps & {
  /** Stable id used to compute z-index from the open-stack registry. */
  stackId: string;
};

/**
 * Single source of truth for the dialog exit-animation duration. JS owns the
 * number; CSS reads it from the `--dialog-exit-duration` custom property on
 * :root, which DialogHost.tsx publishes at module load with a `typeof
 * document !== "undefined"` guard.
 *
 * Bumping the value here updates both the CSS animations and the
 * slot-cleanup timeout — no parsing, no race, no safety margin.
 */
export const DIALOG_EXIT_MS = 130;

/**
 * Stacking constants. Each open dialog occupies STACK_STRIDE z-index units —
 * overlay at OVERLAY_BASE + position*STRIDE, content at CONTENT_BASE +
 * position*STRIDE — so layered overlays alternate above/below content
 * cleanly (sandwich pattern).
 *
 * CONFIRM_OFFSET is added to a confirm/alert dialog's content z-index so
 * it always sits above the regular dialog at the same stack position
 * (typical pattern: confirm dialogs are spawned by their parent dialog and
 * should overlay it).
 */
// Re-exported from the one stacking scale so the dialog's own numbers can't
// drift from the bands the rest of the system is placed against.
export const DIALOG_OVERLAY_BASE_Z = LAYER.dialogOverlay;
export const DIALOG_CONTENT_BASE_Z = LAYER.dialogContent;
export const DIALOG_STACK_STRIDE = LAYER.dialogStride;
export const DIALOG_CONFIRM_OFFSET = 10;
